import { Form, Formik } from "formik";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import AccountTypeItem from "../../components/GeneralComponents/AccountTypeItem";
import Button from "../../components/GeneralComponents/Button";
import DomeCareTitle from "../../components/GeneralComponents/DomeCareTitle";
import ErrorsWrapper from "../../components/GeneralComponents/ErrorsWrapper";
import Loader from "../../components/GeneralComponents/Loader";
import { useAuthContext } from "../../context/AuthContext";
import history from "../../hooks/History";
import {
  setFacilityTypeId,
  setNewAccountRole,
  setRole,
} from "../../redux/slices/authSlice";
import { RootState } from "../../redux/store";
import { AuthFacilityTypeFormValues } from "../../types";
import { useFacilityTypes } from "../../utils/api/General/useFacilityTypes";
import {
  ArabicCode,
  BASE_Images_API_URL,
  DefaultNavigateDelay,
} from "../../utils/constants/CommonConstants";
import useAccountTypeSchema from "../../utils/validations/auth/accountTypeSchema";

interface AccountTypeProps {
  OriginType: string;
}
const AccountType: React.FC<AccountTypeProps> = ({ OriginType }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const { changeShowLeaveAuthRoutePopupAuthState } = useAuthContext();
  const accountTypeSchema = useAccountTypeSchema();
  const { user, token } = useSelector((state: RootState) => state.auth);
  const [activeFacilityType, setActiveFacilityType] = useState<any>(null);

  const {
    createPasswordOrigin,
    isAddingNewAccountToExistingAccount,
    role,
    isCompleteRegister,
  } = useSelector((state: RootState) => state.auth);

  const [initialValues, setInitialValues] =
    useState<AuthFacilityTypeFormValues>({
      accountType: null,
    });

  //?=================Account Type Form Submit ====================
  const handleSubmit = (values: AuthFacilityTypeFormValues) => {
    dispatch(setFacilityTypeId(activeFacilityType?.Id));

    const navigateTo = (path: string) => {
      changeShowLeaveAuthRoutePopupAuthState(false);
      setTimeout(() => {
        history.push(path);
      }, DefaultNavigateDelay);
    };

    const role = activeFacilityType.Code;

    if (OriginType === "account" && isAddingNewAccountToExistingAccount) {
      dispatch(setNewAccountRole(role));
      navigateTo("/account/account-facility-type");
    } else if (
      (OriginType !== "account" && createPasswordOrigin === "register") ||
      isCompleteRegister
    ) {
      dispatch(setRole(role));
      navigateTo("/auth/chosen-facility");
    } else {
      navigateTo("/");
    }
  };

  //?==============================================================
  const checkCommingFromRegisterRoutes = () => {
    if (
      OriginType === "account" ||
      createPasswordOrigin === "register" ||
      isCompleteRegister === true
    ) {
    } else {
      history.push("/");
    }
  };

  useEffect(() => {
    checkCommingFromRegisterRoutes();
  }, [location.pathname, role, navigate]);

  //?=================GET Facilities Types API====================
  const {
    data: facilityTypes,
    isLoading: facilityTypesIsLoading,
    error: facilityTypesError,
  } = useFacilityTypes(token ? token : user?.token);
  //?=================Choose A Facility By Default====================
  useEffect(() => {
    if (facilityTypes && facilityTypes.Data.length > 0) {
      const defaultFacilityType = facilityTypes.Data[0];
      setInitialValues({ accountType: defaultFacilityType });
      setActiveFacilityType(defaultFacilityType);
    }
  }, [facilityTypes]);

  //?=================Leave Popup===================
  useEffect(() => {
    changeShowLeaveAuthRoutePopupAuthState(true);
  }, []);
  return (
    <article className="relative  flex w-full flex-col gap-6 px-8 py-10 xl:w-[75%]">
      {/* <DomeCareTitle /> */}
      <p className="py-8 text-center text-2xl font-bold text-headingTextColor">
        {t("choose_type")}
      </p>
      {facilityTypesIsLoading ? (
        <div className="flex flex-col items-center justify-center">
          <Loader radius={50} />
        </div>
      ) : (
        <Formik
          initialValues={initialValues}
          validationSchema={accountTypeSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ setFieldValue, errors, handleSubmit }) => (
            <Form
              className="flex flex-col gap-5"
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  handleSubmit();
                }
              }}
            >
              {facilityTypes?.Data?.map((facilityType) => {
                const isActive = activeFacilityType?.Id === facilityType?.Id;
                return (
                  <div
                    key={facilityType.Id}
                    onClick={() => {
                      setActiveFacilityType(facilityType);
                      setFieldValue("accountType", facilityType);
                    }}
                  >
                    <AccountTypeItem
                      title={facilityType.Name}
                      desc={`${
                        i18n.language === ArabicCode
                          ? facilityType.DescriptionAr
                          : facilityType.DescriptionEn
                      }`}
                      imgUrl={
                        facilityType.Image
                          ? `${BASE_Images_API_URL}/${facilityType.Image}`
                          : ""
                      }
                      activeImg={
                        facilityType.SelectedImage
                          ? `${BASE_Images_API_URL}/${facilityType.SelectedImage}`
                          : ""
                      }
                      active={isActive}
                      error={errors?.accountType}
                    />
                  </div>
                );
              })}
              <Button
                title={facilityTypesIsLoading ? t("loading") : t("Continue")}
                type="submit"
                variant="primary"
                extraClassName="shadow-cta"
                disabled={facilityTypesIsLoading}
              />
            </Form>
          )}
        </Formik>
      )}
      {facilityTypesError?.message ? (
        <ErrorsWrapper message={facilityTypesError.message} variant="error" />
      ) : null}
    </article>
  );
};

export default AccountType;
